import React, { useEffect, useState } from "react";
import InstructorLayout from "@/components/InstructorLayout";
import FileUploader, { FileTile } from "@/components/FileUploader";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Trash2, Plus, Loader2 } from "lucide-react";

const CATEGORIES = [
  { value: "material_apoyo", label: "Material de apoyo" },
  { value: "lectura", label: "Lectura" },
  { value: "ejercicio", label: "Ejercicio / plantilla" },
  { value: "video", label: "Video" },
  { value: "otro", label: "Otro" },
];

export default function InstructorBiblioteca() {
  const [cohorts, setCohorts] = useState([]);
  const [programId, setProgramId] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("material_apoyo");
  const [pendingTitle, setPendingTitle] = useState("");
  const [pendingDesc, setPendingDesc] = useState("");

  useEffect(() => {
    api.get("/instructor/me/cohorts").then((r) => {
      setCohorts(r.data || []);
      if (r.data?.length && !programId) setProgramId(r.data[0].program_id);
    }).catch(() => {});
  }, []);

  const load = async () => {
    if (!programId) return;
    setLoading(true);
    try {
      const r = await api.get(`/instructor/programs/${programId}/library`);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [programId]);

  const onUploaded = async (f) => {
    if (!programId) return;
    try {
      await api.post(`/instructor/programs/${programId}/library`, {
        program_id: programId,
        file_id: f.id,
        filename: f.filename,
        storage_path: f.storage_path,
        content_type: f.content_type,
        size: f.size,
        category,
        title: pendingTitle || f.filename,
        description: pendingDesc,
      });
      toast.success("Archivo agregado a la biblioteca.");
      setPendingTitle(""); setPendingDesc("");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (entryId) => {
    if (!confirm("¿Eliminar de la biblioteca?")) return;
    try {
      await api.delete(`/instructor/library/${entryId}`);
      toast.success("Eliminado.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const uniquePrograms = Array.from(
    new Map(cohorts.map(c => [c.program_id, { id: c.program_id, label: c.name }])).values()
  );

  return (
    <InstructorLayout>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Instructor</p>
        <h1 className="mt-1 text-3xl font-bold text-white font-display">Biblioteca del programa</h1>
        <p className="mt-1 text-sm text-gray-400">Sube material de apoyo, lecturas, plantillas y videos visibles para los alumnos inscritos.</p>
      </div>

      {uniquePrograms.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center text-sm text-gray-400">
          No tienes cohortes asignadas todavía.
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <div>
              <label className="text-xs font-semibold text-gray-300">Programa</label>
              <select value={programId} onChange={(e) => setProgramId(e.target.value)}
                      data-testid="biblio-program-select"
                      className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                {uniquePrograms.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-300">Categoría</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                      data-testid="biblio-cat-select"
                      className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white">
                {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-300">Título opcional</label>
              <input value={pendingTitle} onChange={(e) => setPendingTitle(e.target.value)}
                     placeholder="Se usará el nombre del archivo si se deja vacío"
                     data-testid="biblio-title-input"
                     className="mt-1 w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
            </div>
          </div>
          <div className="mb-6">
            <textarea rows={2} value={pendingDesc} onChange={(e) => setPendingDesc(e.target.value)}
                      data-testid="biblio-desc-input"
                      placeholder="Descripción breve (opcional)"
                      className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-white" />
          </div>
          <FileUploader kind={category === "video" ? "library_video" : "library"}
                        resourceType="library"
                        resourceId={programId}
                        onUploaded={onUploaded}
                        buttonLabel="Subir a la biblioteca"
                        multiple={false}
                        hint="Máx 50 MB para material general · 200 MB para categoría video." />

          <div className="mt-8">
            <h2 className="text-sm font-bold text-white mb-3 inline-flex items-center gap-2">
              <FolderOpen className="w-4 h-4 text-[#F26A21]" /> Material publicado ({items.length})
            </h2>
            {loading ? (
              <div className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando…</div>
            ) : items.length === 0 ? (
              <p className="text-sm text-gray-400">Aún no has subido material a este programa.</p>
            ) : (
              <div className="grid gap-2">
                {items.map((it) => (
                  <div key={it.id} data-testid={`biblio-row-${it.id}`}
                       className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 p-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-widest text-gray-500">{CATEGORIES.find(c => c.value === it.category)?.label || it.category}</p>
                      <p className="text-sm font-semibold text-white truncate">{it.title}</p>
                      {it.description && <p className="mt-0.5 text-xs text-gray-400 line-clamp-2">{it.description}</p>}
                      <a href={it.url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-[#F26A21] hover:underline">{it.filename}</a>
                    </div>
                    <button onClick={() => remove(it.id)} data-testid={`biblio-remove-${it.id}`}
                            className="p-2 rounded text-gray-400 hover:text-red-300 hover:bg-white/5">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </InstructorLayout>
  );
}
